import { Observable, of, merge } from 'rxjs';
import { withLatestFrom, switchMap, catchError, map, tap, distinctUntilKeyChanged } from 'rxjs/operators';

import { fireGTMEvent } from 'src/features/analytics/hooks/useGtm';
import { guestHomeDeliveryEpic } from 'src/features/hubspot/epics/startCheckout/guest-home-delivery.epic';
import { guestCollectionEpic } from 'src/features/hubspot/epics/startCheckout/guest-collection.epic';

import { MetaAnalyticEvents, MetaEvents, MetaStartedCheckoutGtmEvent, MetaTagManagerEvents } from '../types';

import metaLoggedInUsersEpic from './logged-in-users.epic';

/**
 * Listens for the user starting the checkout process and sends events to Meta/Facebook.
 *
 * The details of how this is triggered depends on the user journey:
 * 1. Logged in users
 *     - The user navigates to the checkout page and their account details
 *       are used to trigger the event.
 * 2. Guests using home delivery
 *    - The user sets their shipping address and proceeds to the payment step.
 * 3. Guests using collection use the `Checkout.SetPendingPayment` event.
 *   - The user sets their billing address and proceeds with payment.
 */
export function metaStartCheckout(action$: Observable<any>, state$: Observable<any>): Observable<any> {
    const loggedInTrigger$ = metaLoggedInUsersEpic(action$, state$);
    const guestHomeDelivery$ = guestHomeDeliveryEpic(action$, state$);
    const guestCollection$ = guestCollectionEpic(action$, state$);

    // Listen for each of the Checkout user journeys
    return merge(loggedInTrigger$, guestHomeDelivery$, guestCollection$).pipe(
        withLatestFrom(state$),

        // Check the event against the Cart ID
        map(([customerInfo, state]) => ({
            ...(customerInfo ?? {}),
            cartId: state.cart?.cartId,
        })),

        // Only trigger the Meta event once per Cart ID.
        distinctUntilKeyChanged('cartId'),

        withLatestFrom(state$),

        // Prepare the Meta GTM event payload
        switchMap(([customerInfo, state]) => {
            const cart_id = state.cart?.cartId ?? undefined;
            const baseUrl = window?.location?.origin ?? '';
            const checkout_url = baseUrl + state.router.current?.urlKey;
            const cart_url = baseUrl + '/basket';

            // Convert cart items to Meta format
            const content_ids: string[] = [];
            const contents: { id: string; quantity: number; item_price?: number }[] = [];

            state.cart?.items?.forEach((cartItem) => {
                const item_id = cartItem?.id;
                const sku = cartItem?.product?.sku;
                const extraProductInformation = state.products?.byIds?.[item_id];

                const sale_price = extraProductInformation?.price?.special_price ?? undefined;
                const maxPrice = extraProductInformation?.price?.maximum_price ?? cartItem?.prices?.price ?? undefined;

                let price = maxPrice;

                if (sale_price) {
                    price = sale_price;
                }

                if (sku) {
                    content_ids.push(sku);
                    contents.push({
                        id: sku,
                        quantity: cartItem?.quantity ?? 1,
                        item_price: price,
                    });
                }
            });

            const gtmEvent: MetaStartedCheckoutGtmEvent = {
                cart_id,
                cart: {
                    cart_id,
                    cart_url,
                    delivery_type: state.cart?.raw?.woodies?.basket_type,
                    items_count: state.cart?.items?.length ?? 0,
                    items_qty: state.cart?.items_qty ?? 0,
                    total_value: state.cart?.totals?.grand_total ?? 0,
                },
                checkout_id: cart_id,
                checkout_url,
                content_ids,
                contents,
                currency: state.cart?.raw?.prices?.grand_total?.currency ?? 'EUR',
                customer_email: customerInfo.email,
                event_name: MetaAnalyticEvents.STARTED_CHECKOUT,
                event: MetaTagManagerEvents.STARTED_CHECKOUT,
                firstname: customerInfo.firstName,
                is_guest: customerInfo.isGuest,
                lastname: customerInfo.lastName,
                session_id: cart_id,
                timestamp: new Date().toISOString(),
                value: state.cart?.totals?.grand_total ?? 0,
            };

            return of(gtmEvent);
        }),

        tap((gtmEvent) => fireGTMEvent(gtmEvent as any)),

        // Send the event to Redux
        map((gtmEvent) => ({
            type: MetaEvents.STARTED_CHECKOUT,
            data: gtmEvent,
        })),

        catchError((error) => {
            return of({
                type: MetaEvents.STARTED_CHECKOUT_ERROR,
                payload: error,
            });
        }),
    );
}
